import { Toggle } from "atom/toggle/Toggle";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import React, { useEffect } from "react";
import {
  IconCheckmarkRoundGreenBackground,
  IconInfoRhombusYellowBackground
} from "src/constants/icons";
import translate from "src/lib/translate";
import HealthSyncBloc from "src/state/HealthSyncBloc/HealthSyncBloc";
import UserPreferencesCubit, {
  chartVisibilityDependencies,
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import WithingsFeaturesCubit from "src/state/WithingsFeaturesCubit/WithingsFeaturesCubit";
import { toast, useBloc } from "src/state/state";
import AppPageTitle from "../AppPageTitle/AppPageTitle";
import {
  PROFILE_URL,
  ProfileMenuItem
} from "../ProfileOverview/ProfileOverview";
import Collection from "../StyledComponents/Collection";
import CollectionItemLink from "../StyledComponents/CollectionItemLink";
import CollectionTitle from "../StyledComponents/CollectionTitle";
import OverlayBackButton from "../StyledComponents/OverlayBackButton";
import {
  AppPopup,
  AppQueryPopupsController
} from "../AppQueryPopups/AppQueryPopupsBloc";
import { Document, getLegalDocumentLink } from "src/lib/getLegalDocumentLink";
import HealthDataSettingsCubit from "./HealthDataSettingsCubit";

const DataSync: FC = () => {
  const [
    // `available` has to come off the state object, not the isAvailable getter:
    // it resolves asynchronously, and only tracked state fields re-render the
    // section below (same trap as DataTabs).
    { available, stepsAuthorized, stepsData },
    { requestAuthorization, checkAndSync }
  ] = useBloc(HealthSyncBloc);
  const [
    ,
    {
      load,
      isChartVisible,
      toggleChartVisibility,
      toggleMeasurementsOnScale,
      showMeasurementsOnScale,
      showBodyCompositionRow,
      showDevicesSection
    }
  ] = useBlocNext(HealthDataSettingsCubit);

  useBlocNext(UserPreferencesCubit, {
    dependencySelector: chartVisibilityDependencies
  });
  useBlocNext(WithingsFeaturesCubit, {
    dependencySelector: (s) => [s.features]
  });
  const allZero = stepsData.every((step) => step.value === 0);

  const handleAuthCheck = () => {
    if (!available) {
      toast.show("health.syncNotAvailable");
      return;
    }

    if (allZero) {
      toast.show("health.noDataCheckPermission");
    }

    if (stepsAuthorized) {
      void requestAuthorization();
      return;
    }

    AppQueryPopupsController.openPopup(AppPopup.healthSyncSetup);
  };

  const handlePrivacyClick = () => {
    AppQueryPopupsController.openPopup(AppPopup.iframe, {
      additionalParameters: {
        url: getLegalDocumentLink({
          document: Document.noticePrivacyPractice,
          contentOnly: true
        }),
        title: translate("registerConsent.noticePrivacyPractice"),
        stay: "false"
      }
    });
  };

  useEffect(() => {
    void checkAndSync();
    void load();
  }, []);

  return (
    <>
      <OverlayBackButton show fallbackUrl={PROFILE_URL} />
      <AppPageTitle title={translate("healthData")}></AppPageTitle>
      <nine-spacer s={"xl"}></nine-spacer>

      <CollectionTitle>
        {translate("healthData.visibilityInCharts")}
      </CollectionTitle>
      <Collection>
        {showBodyCompositionRow && (
          <CollectionItemLink
            label={translate("healthData.showBodyComposition")}
            onClick={() =>
              void toggleChartVisibility(
                UserPreferenceKeys.chartVisibilityBodyComposition
              )
            }
            action={
              <Toggle
                isSelected={isChartVisible(
                  UserPreferenceKeys.chartVisibilityBodyComposition
                )}
              />
            }
          />
        )}
        <CollectionItemLink
          label={translate("healthData.showWaistCircumference")}
          onClick={() =>
            void toggleChartVisibility(
              UserPreferenceKeys.chartVisibilityWaistCircumference
            )
          }
          action={
            <Toggle
              isSelected={isChartVisible(
                UserPreferenceKeys.chartVisibilityWaistCircumference
              )}
            />
          }
        />
        <CollectionItemLink
          label={translate("healthData.showWeight")}
          onClick={() =>
            void toggleChartVisibility(UserPreferenceKeys.chartVisibilityWeight)
          }
          action={
            <Toggle
              isSelected={isChartVisible(
                UserPreferenceKeys.chartVisibilityWeight
              )}
            />
          }
        />
      </Collection>

      {showDevicesSection && (
        <>
          <CollectionTitle className="m-top">
            {translate("healthData.devices")}
          </CollectionTitle>
          <Collection>
            <CollectionItemLink
              label={translate("healthData.showMeasurementsOnScale")}
              onClick={() => void toggleMeasurementsOnScale()}
              action={<Toggle isSelected={showMeasurementsOnScale} />}
            />
          </Collection>
        </>
      )}

      {available && (
        <>
          <CollectionTitle className="m-top">
            {translate("healthData.services")}
          </CollectionTitle>
          <Collection>
            <CollectionItemLink
              label={translate("dataSync.appleHealthPermissions")}
              onClick={handleAuthCheck}
              endIcon={
                stepsAuthorized && !allZero ? (
                  <IconCheckmarkRoundGreenBackground />
                ) : (
                  <IconInfoRhombusYellowBackground />
                )
              }
            />
            <CollectionItemLink
              label={translate("dataSync.learnMore")}
              to={PROFILE_URL + ProfileMenuItem.dataSyncLearnMore}
            />
          </Collection>
        </>
      )}

      <Collection className="mt-1-5">
        <CollectionItemLink
          label={translate("registerConsent.noticePrivacyPractice")}
          onClick={handlePrivacyClick}
        />
      </Collection>
    </>
  );
};

export default DataSync;
